/**
 * Matomo facet filter tracking.
 *
 * Listens for Sprig/htmx swaps of `#results` (article/space list filter
 * changes) and pushes the interaction to Matomo — trackSiteSearch when a
 * keyword is present, trackEvent per active facet otherwise. Pagination
 * ("Load More") swaps `#entries` instead of `#results`, so it's naturally
 * excluded.
 *
 * Tracking fires when the user leaves a facet field (blur) or the page
 * (pagehide/hidden) — not on a fixed idle timer. A timer can't reliably
 * tell "still editing" apart from "done": someone correcting a typo
 * ("max" -> "maxium" -> "maximum") pauses between attempts for all kinds of
 * reasons, and any fixed delay either fires on unfinished words or misses
 * genuinely separate searches. Firing only on blur/navigate means any number
 * of edits while still in the field collapse into exactly one tracked value
 * — whatever it is at the moment they actually move on.
 */
let hasPendingChange = false;

function currentFacetFields() {
  const results = document.getElementById('results');
  if (!results) return [];

  return (results.dataset.facetFields || '')
    .split(',')
    .map((field) => field.trim())
    .filter(Boolean);
}

function trackFacetInteraction() {
  if (!window._paq || typeof window._paq.push !== 'function') return;

  const results = document.getElementById('results');
  if (!results) return;

  const container = results.parentElement;
  if (!container) return;

  const facetFields = currentFacetFields();
  const searchContext = results.dataset.searchContext || '';
  const resultCount = parseInt(results.dataset.resultCount, 10) || 0;

  let keyword = '';
  const activeFacets = [];

  facetFields.forEach((field) => {
    const input = container.querySelector(`[name="${field}"]`);
    const value = input ? input.value.trim() : '';

    if (field === 'keyword') {
      keyword = value;
      return;
    }

    if (value) {
      activeFacets.push({ field, value });
    }
  });

  if (keyword) {
    const category = activeFacets.length
      ? activeFacets.map(({ field, value }) => `${field}:${value}`)
      : false;

    window._paq.push(['trackSiteSearch', keyword, category, resultCount]);
    return;
  }

  activeFacets.forEach(({ field, value }) => {
    window._paq.push(['trackEvent', 'Facet Filter', `${searchContext}: ${field}`, value]);
  });
}

function flushPendingTracking() {
  if (!hasPendingChange) return;
  hasPendingChange = false;
  trackFacetInteraction();
}

function handleAfterSwap(event) {
  const swappedId = event.detail.target && event.detail.target.id;
  if (swappedId !== 'results') return;
  hasPendingChange = true;

  // A dropdown selection often blurs immediately — before the Sprig request
  // that triggered it even finishes — so the focusout listener below may
  // already have run (and no-opped, since nothing was pending yet) by the
  // time this swap lands. If focus isn't on a facet field right now, there's
  // no future focusout coming either, so flush immediately instead of
  // waiting on an event that already happened.
  const activeName = document.activeElement && document.activeElement.name;
  if (!activeName || !currentFacetFields().includes(activeName)) {
    flushPendingTracking();
  }
}

document.addEventListener('htmx:afterSwap', handleAfterSwap);

// Track once the user leaves a facet field or the page — see file header
// for why this replaces a fixed idle-timer debounce.
document.addEventListener('focusout', (event) => {
  const name = event.target && event.target.name;
  if (name && currentFacetFields().includes(name)) {
    flushPendingTracking();
  }
});

window.addEventListener('pagehide', flushPendingTracking);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    flushPendingTracking();
  }
});
